import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PWA_AUTH_KEY = 'pwa_auth_token';
const PWA_USER_KEY = 'pwa_auth_user';

// Detect if running in PWA standalone mode
const isStandalonePWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to format API error details
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build auth headers from localStorage token (PWA fallback)
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem(PWA_AUTH_KEY);
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }, []);

  // Set up axios interceptors for PWA token-based auth + auto token refresh
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem(PWA_AUTH_KEY);
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Auto-refresh token on 401 "Token expired"
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          error.response?.data?.detail === 'Token expired' &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/login')
        ) {
          originalRequest._retry = true;
          try {
            const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
            // Update localStorage token if returned
            if (refreshRes.data?.access_token) {
              localStorage.setItem(PWA_AUTH_KEY, refreshRes.data.access_token);
              originalRequest.headers.Authorization = `Bearer ${refreshRes.data.access_token}`;
            }
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed — force logout
            localStorage.removeItem(PWA_AUTH_KEY);
            localStorage.removeItem(PWA_USER_KEY);
            setUser(false);
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Check localStorage for PWA cached user (instant restore)
    const cachedUser = localStorage.getItem(PWA_USER_KEY);
    const cachedToken = localStorage.getItem(PWA_AUTH_KEY);

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers: cachedToken ? { Authorization: `Bearer ${cachedToken}` } : {}
      });
      setUser(response.data);
      // Update cached user
      localStorage.setItem(PWA_USER_KEY, JSON.stringify(response.data));
    } catch (e) {
      // If server check fails but we have cached data, try to use it temporarily
      if (cachedUser && cachedToken) {
        try {
          setUser(JSON.parse(cachedUser));
        } catch {
          setUser(false);
          localStorage.removeItem(PWA_AUTH_KEY);
          localStorage.removeItem(PWA_USER_KEY);
        }
      } else {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for visibility changes (PWA returning to foreground after OAuth in browser)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !user && isStandalonePWA()) {
        // PWA came back to foreground - check if OAuth completed in browser
        const cachedToken = localStorage.getItem(PWA_AUTH_KEY);
        const cachedUser = localStorage.getItem(PWA_USER_KEY);
        if (cachedToken && cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
            // Verify token is still valid
            checkAuth();
          } catch (e) {
            // silenced
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, checkAuth]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, 
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      // Store token for PWA fallback
      if (response.data?.access_token) {
        localStorage.setItem(PWA_AUTH_KEY, response.data.access_token);
      }
      localStorage.setItem(PWA_USER_KEY, JSON.stringify(response.data));
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async (email, password, name, surname) => {
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`,
        { email, password, name, surname },
        { withCredentials: true }
      );
      setUser(response.data);
      localStorage.setItem(PWA_USER_KEY, JSON.stringify(response.data));
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // silenced
    }
    localStorage.removeItem(PWA_AUTH_KEY);
    localStorage.removeItem(PWA_USER_KEY);
    setUser(false);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/auth/google-session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      const userData = response.data;
      setUser(userData);
      // Store token and user for PWA fallback
      if (userData?.access_token) {
        localStorage.setItem(PWA_AUTH_KEY, userData.access_token);
      }
      localStorage.setItem(PWA_USER_KEY, JSON.stringify(userData));
      return { success: true, user: userData };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const forgotPassword = async (email) => {
    setError(null);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const resetPassword = async (token, newPassword) => {
    setError(null);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: newPassword });
      return { success: true };
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    loginWithGoogle,
    processGoogleSession,
    forgotPassword,
    resetPassword,
    checkAuth,
    setUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
